/**
 * Per-browser UI preferences.
 *
 * Chrome state — is the sidebar collapsed, is a reference panel folded — is a
 * property of this browser, not of the account, so it lives in localStorage
 * rather than travelling to the server. Losing it costs a click.
 *
 * Shaped after the `tokens` object in `api.ts`: a small typed accessor rather
 * than a hook, so it can be read during the first render to set initial state
 * without a flash of the wrong layout. Keys carry a `compendium.` prefix;
 * `api.ts` predates the convention and still uses `compendium.access` /
 * `compendium.refresh`, which happen to match it.
 *
 * Every read and write is guarded. `localStorage` throws outright — not
 * returns null — in a private window with site data blocked, and chrome that
 * cannot remember its state must still render.
 */

const SIDEBAR_KEY = "compendium.sidebar";
const PANEL_PREFIX = "compendium.panel.";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* nothing to do: the preference simply will not persist */
  }
}

export const prefs = {
  /** Whether the sidebar is showing as a narrow icon rail. */
  sidebarCollapsed: (): boolean => read(SIDEBAR_KEY) === "collapsed",

  setSidebarCollapsed(collapsed: boolean): void {
    write(SIDEBAR_KEY, collapsed ? "collapsed" : "expanded");
  },

  /**
   * Whether a collapsible panel is open.
   *
   * ``fallback`` decides for a panel the reader has never touched, which is how
   * a screen states that its controls start open and its reference panels
   * start folded. Once touched, the reader's choice wins for good.
   */
  panelOpen(key: string, fallback: boolean): boolean {
    const stored = read(PANEL_PREFIX + key);
    if (stored === "open") return true;
    if (stored === "closed") return false;
    return fallback;
  },

  setPanelOpen(key: string, open: boolean): void {
    write(PANEL_PREFIX + key, open ? "open" : "closed");
  },
};
